// src/lib/api.js
// A small fetch wrapper that auto-picks the backend base (dev/prod), handles
// credentials, and CSRF for unsafe methods.

/**
 * How base URL is chosen (in order):
 * 1) If running on localhost and REACT_APP_USE_PROXY !== 'false', use relative '' (dev proxy)
 * 2) Otherwise use REACT_APP_BACKEND_BASE or REACT_APP_API_BASE_URL, if provided
 * 3) If running on localhost without proxy, default to http://localhost:8000
 * 4) Otherwise, use same-origin (window.location.origin)
 */
function resolveBase() {
  const envBase = process.env.REACT_APP_BACKEND_BASE || process.env.REACT_APP_API_BASE_URL;

  const host = window.location.hostname;
  const isLocalhost = host === "localhost" || host === "127.0.0.1";
  if (isLocalhost) {
    if (process.env.REACT_APP_USE_PROXY !== "false") {
      // CRA dev proxy or Vite proxy configured; use relative paths
      console.log("[api] localhost dev; using relative API base through proxy");
      return "";
    }

    if (envBase) {
      console.log("[api] localhost dev without proxy; using explicit backend base");
      return envBase.replace(/\/$/, "");
    }

    console.log("[api] localhost dev without proxy; defaulting to http://localhost:8000");
    return "http://localhost:8000";
  }

  if (envBase) return envBase.replace(/\/$/, "");

  // Production: same origin
  console.log("[api] production; using same-origin API base");
  return window.location.origin.replace(/\/$/, "");
}
// Always use relative paths so CRA dev proxy forwards to Django.
// In production (when you build and serve the frontend behind the same domain),
// these remain same-origin relative paths as well.
//function resolveBase() {
//  return ""; // <-- no scheme/host/port
//}

export const API_BASE = resolveBase();

// ---- CSRF helpers ----
export function getCsrfToken() {
  const m = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

export async function ensureCsrf() {
  // Hit the CSRF bootstrap endpoint so the browser gets the csrftoken cookie.
  // No-op if server doesn't expose it, but recommended.
  try {
    fetch(`${API_BASE}/auth/csrf/`, { credentials: "include" });
  } catch (_) {
    // ignore bootstrap failures (server might set cookie by other means)
  }
}

// ---- Core fetch wrapper (hardened) ----
async function request(
  path,
  { method = "GET", headers = {}, body, credentials = "include", json = true } = {}
) {
  const url = `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  const isGetLike = method.toUpperCase() === "GET" || method.toUpperCase() === "HEAD";

  // Base headers
  const finalHeaders = { ...(json ? { "Content-Type": "application/json" } : {}), ...headers };

  // CSRF for non-GETs
  if (!isGetLike) {
    const csrf = getCsrfToken();
    if (csrf) finalHeaders["X-CSRFToken"] = csrf;
  }

  // OAuth Bearer (skip when requesting a token)
  const isTokenEndpoint = url.includes("/o/token");
  const accessToken = !isTokenEndpoint ? localStorage.getItem("access_token") : null;
  if (accessToken && !finalHeaders.Authorization) {
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  // Fire the request (surface true network errors instead of "HTTP 0")
  let resp;
  try {
    resp = await fetch(url, {
      method,
      headers: finalHeaders,
      body: json && body && typeof body !== "string" ? JSON.stringify(body) : body,
      credentials, // keep cookies if you also allow session endpoints elsewhere
    });
  } catch (e) {
    const err = new Error(`Network error: ${e?.name || ""} ${e?.message || e}`);
    err.status = 0;
    err.url = url;
    console.debug(`[api] ${method} ${url} -> network error`, err);
    throw err;
  }

  // Read/parse body safely
  const status = resp.status ?? 0;
  const ok = "ok" in resp ? resp.ok : status >= 200 && status < 300;
  const contentType = resp.headers?.get?.("content-type") || "";
  let raw = null;
  let data = null;

  try {
    if (contentType.includes("application/json")) {
      data = await resp.json();
    } else {
      raw = await resp.text();
      try { data = raw ? JSON.parse(raw) : null; } catch { /* non-JSON; leave raw */ }
    }
  } catch (e) {
    console.debug(`[api] ${method} ${url} -> ${status} (body read error: ${e?.message || e})`);
  }

  console.debug(`[api] ${method} ${url} -> ${status}`, data ?? raw ?? "");

  if (!ok) {
    const msg = (data && (data.detail || data.error)) || raw || `HTTP ${status}`;
    const err = new Error(msg);
    err.status = status;
    err.url = url;
    err.payload = data ?? raw;
    throw err;
  }

  // Prefer parsed JSON; otherwise return raw text + status for callers that need it
  return data ?? { raw, status };
}

// ---- Convenience methods ----
export const api = {
  base: API_BASE,
  ensureCsrf,
  get: (path, opts) => request(path, { method: "GET", ...opts }),
  getJson: (path, opts) => request(path, { method: "GET", json: true, ...opts }),
  post: (path, body, opts) => request(path, { method: "POST", body, ...opts }),
  postJson: (path, body, opts) => request(path, { method: "POST", body, json: true, ...opts }),
  patchJson: (path, body, opts) => request(path, { method: "PATCH", body, json: true, ...opts }),
  putJson: (path, body, opts) => request(path, { method: "PUT", body, json: true, ...opts }),
  delete: (path, opts) => request(path, { method: "DELETE", ...opts }),
};

// Example usage:
// await api.ensureCsrf();
// const session = await api.postJson('/payments/create-checkout-session/', { artwork_id: 123 });

// 1) Start onboarding
export async function startOnboarding() {
  const { url } = await api.postJson("/payments/connect/onboard", {});
  window.location.href = url;  // redirect to Stripe-hosted onboarding
}

// 2) Check status (show “Connected” / “Action needed”)
export async function loadConnectStatus() {
  return api.getJson("/payments/connect/status");
}

// 3) Open Express dashboard (manage bank, tax forms)
export async function openPayoutDashboard() {
  const { url } = await api.postJson("/payments/connect/dashboard", {});
  window.open(url, "_blank", "noopener");
}
